import storage from "../storage";

const DEVICE_ID_KEY = "device_id";
const COOKIE_MAX_AGE_DAYS = 3650;

const getCookie = (name) => {
	const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
	return match ? decodeURIComponent(match[1]) : null;
};

const setCookie = (name, value) => {
	const expires = new Date(Date.now() + COOKIE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000).toUTCString();
	document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
};

const generateDeviceId = () => {
	if (typeof crypto !== "undefined" && crypto.randomUUID) {
		return crypto.randomUUID();
	}
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};

const getDeviceId = () => {
	// localStorage and the cookie back each other up - if one gets wiped
	// (cache clear, storage-only reset) the other keeps the id alive, and
	// both get re-synced below so a single missing copy self-heals.
	let id = storage.get(DEVICE_ID_KEY) || getCookie(DEVICE_ID_KEY);
	if (!id) {
		id = generateDeviceId();
	}
	storage.set(DEVICE_ID_KEY, id);
	setCookie(DEVICE_ID_KEY, id);
	return id;
};

const parseAndroidModel = (ua) => {
	const match = ua.match(/Android [^;]+;\s*([^)]+)\)/i);
	if (!match) return null;
	return match[1].split("Build/")[0].trim();
};

const getDeviceNameFromUA = () => {
	const ua = (window.navigator && window.navigator.userAgent) || "";
	if (/ipad/i.test(ua)) return "iPad";
	if (/iphone/i.test(ua)) return "iPhone";
	if (/ipod/i.test(ua)) return "iPod";
	if (/android/i.test(ua)) return parseAndroidModel(ua) || "Android device";
	if (/windows/i.test(ua)) return "Windows PC";
	if (/mac os x/i.test(ua)) return "Mac";
	if (/linux/i.test(ua)) return "Linux PC";
	return "Unknown device";
};

const getDeviceName = async () => {
	const uaData = window.navigator && window.navigator.userAgentData;
	if (uaData && uaData.getHighEntropyValues) {
		try {
			const { model } = await uaData.getHighEntropyValues(["model"]);
			if (model) return model;
		} catch (e) {
			// userAgentData unsupported/blocked, fall back to UA parsing below
		}
	}
	return getDeviceNameFromUA();
};

const getPermissionStatus = () => {
	if (!window.navigator || !window.navigator.permissions || !window.navigator.permissions.query) {
		return Promise.resolve(null);
	}
	return window.navigator.permissions.query({ name: "geolocation" }).catch(() => null);
};

// A single, app-wide location subscription. Running more than one
// watchPosition/getCurrentPosition call concurrently confuses the browser's
// permission UI (duelling prompts, flapping permission state), so every part
// of the app that needs live location goes through this one tracker instead
// of calling navigator.geolocation directly.
let currentLocation = null;
let watchId = null;
const locationListeners = new Set();
const permissionLostListeners = new Set();

const subscribeLocation = (onUpdate) => {
	locationListeners.add(onUpdate);
	if (currentLocation) onUpdate(currentLocation);
	return () => locationListeners.delete(onUpdate);
};

// Fires when a permission that was previously granted stops working mid
// session (revoked from browser/OS settings while the app is open). This is
// the reliable signal - the Permissions API's `onchange` event isn't fired
// consistently for every way a user can revoke location access.
const subscribePermissionLost = (onLost) => {
	permissionLostListeners.add(onLost);
	return () => permissionLostListeners.delete(onLost);
};

const stopLocationTracking = () => {
	if (watchId !== null && window.navigator && window.navigator.geolocation) {
		window.navigator.geolocation.clearWatch(watchId);
	}
	watchId = null;
};

const startLocationTracking = () => {
	if (watchId !== null || !window.navigator || !window.navigator.geolocation) {
		return;
	}
	watchId = window.navigator.geolocation.watchPosition(
		(position) => {
			currentLocation = [position.coords.latitude, position.coords.longitude];
			locationListeners.forEach((cb) => cb(currentLocation));
		},
		(error) => {
			if (error && error.code === error.PERMISSION_DENIED) {
				currentLocation = null;
				stopLocationTracking();
				locationListeners.forEach((cb) => cb(null));
				permissionLostListeners.forEach((cb) => cb(error));
			}
		},
		{ enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
	);
};

// Only call this from a user gesture (or when permission is already
// "granted") - never eagerly, otherwise a dismissed native prompt leaves the
// user stuck with no way to ask again.
const requestLocation = () =>
	new Promise((resolve, reject) => {
		if (!window.navigator || !window.navigator.geolocation) {
			reject({ code: 0 });
			return;
		}
		window.navigator.geolocation.getCurrentPosition(
			(position) => {
				const location = [position.coords.latitude, position.coords.longitude];
				currentLocation = location;
				locationListeners.forEach((cb) => cb(location));
				startLocationTracking();
				resolve(location);
			},
			(error) => {
				if (error && error.code === error.PERMISSION_DENIED) {
					currentLocation = null;
					stopLocationTracking();
				}
				reject(error);
			},
			{ enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
		);
	});

// Safe to call eagerly (e.g. on app boot) - only starts tracking when
// permission is already granted, so it can never trigger a native prompt.
const trackLocationIfAllowed = async () => {
	const permissionStatus = await getPermissionStatus();
	if (permissionStatus && permissionStatus.state === "granted") {
		startLocationTracking();
	}
};

export default {
	getDeviceId,
	getDeviceName,
	getPermissionStatus,
	subscribeLocation,
	subscribePermissionLost,
	requestLocation,
	trackLocationIfAllowed,
};
